import React, { FC, useEffect, useState } from 'react';
import { useIntentDetection } from '../hooks/useIntentDetection';
import { useSignupFlow } from '../hooks/useSignupFlow';
import { useRedirectHandler } from '../hooks/useRedirectHandler';
import QuestionOne from './QuestionOne';
import QuestionTwo from './QuestionTwo';
import GoalSelection from './GoalSelection';
import { UserProfile, UserIntent, UserGoal } from '../types';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Backdrop, Grid, Modal, CircularProgress } from '@mui/material';
import { callApi } from 'src/core/callApi';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    onboardingModal: {
      position: 'relative',
      width: '100%',
      animation: 'fade-scale-in 0.3s ease forwards',
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(2),
    },
    centeredModal: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      outline: 'none',
      width: '100%',
    },
  })
);

const pageStyle = {
  maxWidth: 600,
  mx: 'auto',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  pb: { xs: 'env(safe-area-inset-bottom)', sm: 4 },
  pt: { xs: 'env(safe-area-inset-top)', sm: 4 },
  px: { xs: 2, sm: 4 },
  height: { xs: 'var(--mobile-dashboard-map-height)', sm: '90vh' },
  maxHeight: { sm: '550px' },
};

const UserOnboardingModal: FC<{
  setShowOnboardingModal: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ setShowOnboardingModal }) => {
  const classes = useStyles();
  const initialIntent = useIntentDetection();
  const [intent, setIntent] = useState<UserIntent | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    if (initialIntent) {
      setIntent(initialIntent);
    }
  }, [initialIntent]);

  const {
    step,
    profile,
    isComplete,
    updateProfile,
    nextStep,
    prevStep,
    shouldSkipQ2,
  } = useSignupFlow(intent || initialIntent);
  const { executeRedirect } = useRedirectHandler();

  const syncUserProfile = async (profile: UserProfile) => {
    const userData = {
      userDescription:
        profile.userType != 'unclassified' ? profile.userType : profile.customDescription,
      strategy: profile.strategy,
      role: profile.role != 'unclassified' ? profile.role : profile.customRole,
      goal: profile.goal != 'unclassified' ? profile.goal : profile.customGoal,
    };

    try {
      await callApi(
        'https://8fl0ic90c1.execute-api.us-east-2.amazonaws.com/default/postGoogleAuthDataUpdate',
        {
          body: [
            {
              token: localStorage.getItem('auth-token'),
              user_description: userData.userDescription,
              user_strategy: userData.strategy,
              user_role: userData.role,
              user_goal: userData.goal,
            },
          ],
          method: 'post',
        },
        true
      );

      localStorage.removeItem('user-description-form-to-fill');
    } catch (error) {
      console.error('Error updating user description:', error);
    }
  };

  // Handle completion
  useEffect(() => {
    const handleCompletion = async () => {
      if (isComplete && profile.userType && intent) {
        setIsSyncing(true);
        try {
          await syncUserProfile(profile as UserProfile);
          executeRedirect(intent, profile as UserProfile);
          setShowOnboardingModal(false);
        } catch (error) {
          console.error('Error during completion:', error);
          // You might want to show an error message to the user here
        } finally {
          setIsSyncing(false);
        }
      }
    };

    handleCompletion();
  }, [isComplete, profile, intent, executeRedirect, step]);

  const handleIntentUpdate = (newIntent: UserIntent) => {
    setIntent(newIntent);
  };

  return (
    <Modal
      open={true}
      aria-labelledby="auth-modal"
      aria-describedby="auth-modal-description"
      BackdropComponent={Backdrop}
    >
      <div className={classes.centeredModal}>
        <div className={`${classes.onboardingModal} modal-custom`}>
          {isSyncing ? (
            <div className={classes.loadingContainer}>
              <CircularProgress />
              <div>Saving your preferences...</div>
            </div>
          ) : (
            <>
              {step === 1 && (
                <QuestionOne
                  value={profile.userType}
                  onChange={data => updateProfile(data)}
                  onNext={nextStep}
                  pageStyle={pageStyle}
                />
              )}

              {step === 2 && !shouldSkipQ2 && (
                <QuestionTwo
                  userType={profile.userType!}
                  value={{
                    strategy: profile.strategy,
                    role: profile.role,
                  }}
                  onChange={data => updateProfile(data)}
                  onNext={nextStep}
                  onBack={prevStep}
                  pageStyle={pageStyle}
                />
              )}

              {step === 3 && !intent?.skipOnboarding && (
                <GoalSelection
                  profile={profile}
                  intent={intent || initialIntent}
                  value={profile.goal}
                  onChange={data => updateProfile(data)}
                  onNext={nextStep}
                  onBack={prevStep}
                  onIntentUpdate={handleIntentUpdate}
                  pageStyle={pageStyle}
                />
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default UserOnboardingModal;
